import { useState, useEffect, useRef } from "react";
import {
  generateToken,
  revokeToken,
} from "@/components/main/utils/tokens/TokensApi";
import { TokenList } from "@/components/main/utils/tokens/TokenList";
import { TokenDialog } from "@/components/main/utils/tokens/TokenDialog";
import { AccessConnectPanel } from "@/components/main/utils/tokens/AccessConnectPanel";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import { useTokensStore } from "@/components/main/stores/tokensStore";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { useAppInitializationStore } from "@/components/main/stores/appInitializationStore";
import { useShallow } from "zustand/react/shallow";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import {
  ColumnBottomSpacer,
  ColumnTopSpacer,
} from "@/components/main/pages/pageColumn";

export const AccessPage = () => {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const {
    tokens,
    fetchTokensList,
    removeToken,
    newToken,
    openDialog,
    resetDialog,
    setGeneratedToken,
    setIsGeneratingToken,
  } = useTokensStore(
    useShallow((state) => ({
      tokens: state.tokens,
      fetchTokensList: state.fetchTokensList,
      removeToken: state.removeToken,
      newToken: state.newToken,
      openDialog: state.openDialog,
      resetDialog: state.resetDialog,
      setGeneratedToken: state.setGeneratedToken,
      setIsGeneratingToken: state.setIsGeneratingToken,
    })),
  );
  const { isAppInitialized, tokensInitialized } = useAppInitializationStore(
    useShallow((state) => ({
      isAppInitialized: state.isAppInitialized(),
      tokensInitialized: state.tokensInitialized,
    })),
  );
  const initializedRef = useRef(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: "",
    tokenId: "",
  });

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      void fetchTokensList({
        showLoading: !isAppInitialized && !tokensInitialized,
        refresh: tokensInitialized,
      });
    }
  }, [fetchTokensList, tokensInitialized, isAppInitialized]);

  const handleGenerateToken = async () => {
    setIsGeneratingToken(true);
    try {
      if (!newToken.description.trim()) {
        throw new Error("Token description is required");
      }

      if (!activeOrgId) {
        throw new Error("No active organization to bind the token to");
      }
      const result = await generateToken(
        newToken.expiration_time,
        newToken.description,
        newToken.scope,
        activeOrgId,
      );

      setGeneratedToken(result.token);
    } catch (error) {
      showNotificationGlobal(
        (error as Error).message || "Failed to generate token",
        "error",
      );
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const handleTokenDialogDone = async () => {
    const wasTokenGenerated = useTokensStore.getState().generatedToken !== null;
    resetDialog();
    await fetchTokensList({ showLoading: false, refresh: true });

    if (wasTokenGenerated) {
      showNotificationGlobal("Token created successfully", "success");
    }
  };

  const handleRevokeToken = (tokenId: string) => {
    setConfirmDialog({
      open: true,
      message:
        "Are you sure you want to revoke this token? This action cannot be undone.",
      tokenId,
    });
  };

  const handleConfirmAction = async () => {
    const { tokenId } = confirmDialog;
    setConfirmDialog((prev) => ({ ...prev, open: false }));

    try {
      await revokeToken(tokenId);
      removeToken(tokenId);
      showNotificationGlobal("Token revoked successfully", "success");
      await fetchTokensList({ showLoading: false, refresh: true });
    } catch {
      showNotificationGlobal("Failed to revoke token", "error");
    }
  };

  return (
    <>
      <div className="relative flex flex-col items-center w-full h-full bg-bg overflow-y-auto px-6">
        <ColumnTopSpacer />
        <div
          className="w-full select-none"
          style={{
            maxWidth: "45rem",
            paddingTop: "2.5rem",
            paddingBottom: "2.5rem",
          }}
        >
          <div className="mb-4">
            <h1 className="text-fg-title leading-none">Access</h1>
            <p className="text-fg-muted-regular mt-1.5">
              Manage client connections and API tokens for secure access.
            </p>
          </div>

          <AccessConnectPanel />

          <div className="mt-6">
            <p className="text-fg-muted-regular leading-relaxed mb-4">
              For the Python SDK, REST API, and scripts. Carries your
              account&apos;s permissions.
            </p>

            {tokensInitialized && (
              <TokenList
                tokens={tokens}
                handleRevokeToken={handleRevokeToken}
                onGenerate={openDialog}
              />
            )}
          </div>
        </div>
        <ColumnBottomSpacer />
      </div>

      <TokenDialog
        onGenerate={handleGenerateToken}
        onDone={handleTokenDialogDone}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        confirmText="Revoke"
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};
